"use client";

import { c as _c } from "react/compiler-runtime";
import { SystemAdjustmentPill } from "@/components/common/SystemAdjustmentPill";
import { USER_PAGE_TAB_IDS } from "@/components/user/layout/userTabs.config";
import CommonProfileLink from "@/components/user/utils/CommonProfileLink";
import type { ProfileActivityLogRatingEdit } from "@/entities/IProfile";
import { ProfileActivityLogRatingEditContentChangeReason } from "@/entities/IProfile";
import { formatNumberWithCommas } from "@/helpers/Helpers";
import { RateMatter } from "@/types/enums";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import ProfileActivityLogItemAction from "./utils/ProfileActivityLogItemAction";
enum ProfileActivityLogRateType {
  ADDED = "ADDED",
  REMOVED = "REMOVED",
}
const ACTION: Record<ProfileActivityLogRateType, string> = {
  [ProfileActivityLogRateType.ADDED]: "added",
  [ProfileActivityLogRateType.REMOVED]: "reduced"
};
const LOG_MATTER_STR: Record<RateMatter, string> = {
  [RateMatter.REP]: "Rep",
  [RateMatter.NIC]: "NIC",
  [RateMatter.DROP_REP]: "Drop Rep"
};
const TO_FROM: Record<ProfileActivityLogRateType, string> = {
  [ProfileActivityLogRateType.ADDED]: "to",
  [ProfileActivityLogRateType.REMOVED]: "from"
};
export default function ProfileActivityLogRate(t0) {
  const $ = _c(57);
  const {
    log,
    user
  } = t0;
  const isSystemAdjustment = log.contents.change_reason === ProfileActivityLogRatingEditContentChangeReason.LOST_TDH;
  const searchParams = useSearchParams();
  const getRatingType = () => log.contents.new_rating < log.contents.old_rating ? ProfileActivityLogRateType.REMOVED : ProfileActivityLogRateType.ADDED;
  const ratingType = getRatingType();
  const change = log.contents.new_rating - log.contents.old_rating;
  const isChangePositive = change > 0;
  let t1;
  if ($[0] !== change) {
    t1 = formatNumberWithCommas(Math.abs(change));
    $[0] = change;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const changeStr = t1;
  const isNewRatingPositive = log.contents.new_rating > 0;
  const isNewRatingNegative = log.contents.new_rating < 0;
  let t2;
  if ($[2] !== log.contents.new_rating) {
    t2 = formatNumberWithCommas(log.contents.new_rating);
    $[2] = log.contents.new_rating;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const newRatingStr = t2;
  let t3;
  if ($[4] !== isNewRatingNegative || $[5] !== isNewRatingPositive) {
    t3 = () => {
      if (isNewRatingPositive) {
        return "tw-text-green";
      } else {
        if (isNewRatingNegative) {
          return "tw-text-red";
        } else {
          return "tw-text-iron-400";
        }
      }
    };
    $[4] = isNewRatingNegative;
    $[5] = isNewRatingPositive;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const getTotalRatingClass = t3;
  const handleOrWallet = log.target_profile_handle ?? "";
  let t4;
  if ($[7] !== searchParams) {
    t4 = (searchParams?.get("user") as string)?.toLowerCase();
    $[7] = searchParams;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== handleOrWallet) {
    t5 = handleOrWallet.toLowerCase();
    $[9] = handleOrWallet;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const isCurrentUser = t4 === t5;
  const tabTarget = log.contents.rating_matter === RateMatter.REP ? USER_PAGE_TAB_IDS.REP : USER_PAGE_TAB_IDS.IDENTITY;
  let t6;
  if ($[11] !== log.profile_handle || $[12] !== log.proxy_handle || $[13] !== user) {
    const getProxyHandle = () => {
      if (!log.proxy_handle) {
        return null;
      }
      if (!user) {
        return log.proxy_handle;
      }
      if (user.toLowerCase() === log.proxy_handle.toLowerCase()) {
        return log.profile_handle;
      }
      return log.proxy_handle;
    };
    t6 = getProxyHandle();
    $[11] = log.profile_handle;
    $[12] = log.proxy_handle;
    $[13] = user;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const proxyHandle = t6;
  let t7;
  if ($[15] !== user) {
    t7 = user?.toLowerCase();
    $[15] = user;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== log.proxy_handle) {
    t8 = log.proxy_handle?.toLowerCase();
    $[17] = log.proxy_handle;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const isSelfProxy = t7 === t8;
  let t9;
  if ($[19] !== isSelfProxy || $[20] !== proxyHandle) {
    t9 = !!proxyHandle && isSelfProxy && <Link href={`/${proxyHandle}`} className="tw-whitespace-nowrap tw-text-xs tw-font-medium tw-text-iron-400 tw-no-underline">(Proxy for {proxyHandle})</Link>;
    $[19] = isSelfProxy;
    $[20] = proxyHandle;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const t10 = ACTION[ratingType];
  let t11;
  if ($[22] !== t10) {
    t11 = <ProfileActivityLogItemAction action={t10} />;
    $[22] = t10;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  const t12 = `${isChangePositive ? "tw-text-green" : "tw-text-red"} tw-text-base tw-font-medium`;
  let t13;
  if ($[24] !== changeStr || $[25] !== t12) {
    t13 = <span className={t12}>{changeStr}</span>;
    $[24] = changeStr;
    $[25] = t12;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  const t14 = `${getTotalRatingClass()} tw-whitespace-nowrap tw-text-base tw-font-medium`;
  let t15;
  if ($[27] !== newRatingStr || $[28] !== t14) {
    t15 = <span className={t14}>(total {newRatingStr})</span>;
    $[27] = newRatingStr;
    $[28] = t14;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== log.contents.rating_category || $[31] !== log.contents.rating_matter) {
    t16 = log.contents.rating_matter === RateMatter.REP && <span className="tw-whitespace-nowrap tw-text-base tw-font-medium tw-text-iron-200">{log.contents.rating_category}</span>;
    $[30] = log.contents.rating_category;
    $[31] = log.contents.rating_matter;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  const t17 = LOG_MATTER_STR[log.contents.rating_matter];
  let t18;
  if ($[33] !== t17) {
    t18 = <ProfileActivityLogItemAction action={t17} />;
    $[33] = t17;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  const t19 = TO_FROM[ratingType];
  let t20;
  if ($[35] !== t19) {
    t20 = <ProfileActivityLogItemAction action={t19} />;
    $[35] = t19;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  let t21;
  if ($[37] !== handleOrWallet || $[38] !== isCurrentUser || $[39] !== tabTarget) {
    t21 = <CommonProfileLink handleOrWallet={handleOrWallet} isCurrentUser={isCurrentUser} tabTarget={tabTarget} />;
    $[37] = handleOrWallet;
    $[38] = isCurrentUser;
    $[39] = tabTarget;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  let t22;
  if ($[41] !== isSelfProxy || $[42] !== proxyHandle) {
    t22 = !!proxyHandle && !isSelfProxy && <Link href={`/${proxyHandle}`} className="tw-whitespace-nowrap tw-text-xs tw-font-medium tw-text-iron-400 tw-no-underline">(Proxy: {proxyHandle})</Link>;
    $[41] = isSelfProxy;
    $[42] = proxyHandle;
    $[43] = t22;
  } else {
    t22 = $[43];
  }
  let t23;
  if ($[44] !== isSystemAdjustment) {
    t23 = isSystemAdjustment && <SystemAdjustmentPill />;
    $[44] = isSystemAdjustment;
    $[45] = t23;
  } else {
    t23 = $[45];
  }
  let t24;
  if ($[46] !== t11 || $[47] !== t13 || $[48] !== t15 || $[49] !== t16 || $[50] !== t18 || $[51] !== t20 || $[52] !== t21 || $[53] !== t22 || $[54] !== t23 || $[55] !== t9) {
    t24 = <>{t9}{t11}{t13}{t15}{t16}{t18}{t20}{t21}{t22}{t23}</>;
    $[46] = t11;
    $[47] = t13;
    $[48] = t15;
    $[49] = t16;
    $[50] = t18;
    $[51] = t20;
    $[52] = t21;
    $[53] = t22;
    $[54] = t23;
    $[55] = t9;
    $[56] = t24;
  } else {
    t24 = $[56];
  }
  return t24;
}